/**
 * Client-side URL validator that mirrors server-side validations
 */

const MAX_URL_LENGTH = 2048;

/**
 * Normalize URL by adding protocol if missing
 * @param {string} value
 * @returns {string}
 */
const normalizeUrl = value => {
  if (value && value.length > 0 && value.indexOf("://") === -1) {
    return `https://${value}`;
  }
  return value;
};

/**
 * Check if value is a valid HTTP URL
 * @param {string} value
 * @returns {boolean}
 */
const hasValidScheme = value => /^https?:\/\//i.test(value);

/**
 * Check if hostname contains only valid characters
 * @param {string} value
 * @returns {boolean}
 */
const hasValidHostname = value => {
  if (!value) return false;

  const domainRegex = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})+$/;
  try {
    const { hostname } = new URL(value);
    return domainRegex.test(hostname);
  } catch {
    return false;
  }
};

/**
 * Validate URL and return validation results
 * @param {string} value
 * @returns {Object} Validation result with isValid, error message, and warning
 */
const validateUrl = value => {
  const normalizedUrl = normalizeUrl(value);
  // Check for blank URL
  if (!normalizedUrl || normalizedUrl.trim() === "") {
    return {
      isValid: false,
      error: "URL cannot be blank"
    };
  }

  // Check URL length to prevent DoS attacks
  if (normalizedUrl.length > MAX_URL_LENGTH) {
    return {
      isValid: false,
      error: "URL is too long"
    };
  }

  // Check if it's a valid HTTP URL
  if (normalizedUrl.length > 0 && !hasValidScheme(normalizedUrl)) {
    return {
      isValid: false,
      error: "The website link is not a valid URL."
    };
  }

  // Check if hostname is valid
  if (!hasValidHostname(normalizedUrl)) {
    return {
      isValid: false,
      error: "The website link is not a valid URL."
    };
  }

  // URL is valid
  return { isValid: true, error: null };
};

export default validateUrl;
